import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { applyDiscountCode } from '@theme/cart-discount';
import { popupExperiment } from '@theme/newsletter-popup-experiment';
import {
  WELCOME_CAMPAIGN,
  WELCOME_TRIGGER_DELAY_MS,
  addAnalyticsTrigger,
  getPopupOpeningDecision,
  getWelcomeTriggerSettings,
  hasReachedWelcomeScrollDepth,
  isLikelyEmail,
  isWelcomeArticlePage,
  isWelcomePopupEligible,
  newsletterSignupEndpoint,
  postWelcomeSignup,
  runOncePerSession,
  shouldBlockPopupClose,
  shouldReopenAfterWelcomeSignupSuccess,
  submitWelcomeShopifyFirst,
  supportsWelcomeExitIntent,
  welcomeSignupRequestBody,
  WELCOME_SIGNUP_TIMEOUT_MS,
} from '@theme/newsletter-popup-logic';

const SECOND = 1000;
const DAY = 24 * 60 * 60 * SECOND;
const RETRY_DELAY = SECOND;
const COUNTRY_REQUEST_TIMEOUT = 2500;
const SHOPIFY_CAPTCHA_TIMEOUT = 15000;
const INTERNATIONAL_REQUEST_TIMEOUT = 12000;
const INTERNATIONAL_MIN_DWELL = 1500;
const INTERNATIONAL_SUBMIT_COOLDOWN = 15000;

/**
 * Theme-native Kokoruno audience popup.
 *
 * Shopify's browsing-context endpoint chooses welcome vs international
 * waitlist. Welcome signups go to Profit OS, which writes the Shopify customer
 * through Admin GraphQL and returns whether WELCOME10 still applies. Klaviyo
 * receives a best-effort secondary sync after that succeeds. Unsupported
 * visitors join a separate Klaviyo waitlist and never enter the welcome flow.
 */
class NewsletterPopup extends DialogComponent {
  requiredRefs = [
    'dialog',
    'teaserShell',
    'teaserButton',
    'teaserCloseButton',
    'closeButton',
    'welcomeShell',
    'initialState',
    'successState',
    'successHeading',
    'emailInput',
    'submitButton',
    'errorMessage',
    'errorText',
    'couponCode',
    'copyButton',
    'copyFeedback',
    'welcomeHoneypot',
    'welcomeOfferSuccess',
    'welcomeExistingSuccess',
    'existingSuccessHeading',
    'welcomeSuccessContinueButton',
    'existingSuccessContinueButton',
    'internationalShell',
    'internationalHeading',
    'internationalInitialState',
    'internationalSuccessState',
    'internationalSuccessHeading',
    'internationalTagsInput',
    'internationalEmailInput',
    'internationalSubmitButton',
    'internationalHoneypot',
    'internationalErrorMessage',
    'internationalErrorText',
    'continueButton',
    'successContinueButton',
  ];

  #abortController = new AbortController();
  #triggerAbortController;
  #openTimer;
  #activeTimerStartedAt = 0;
  #remainingActiveDelay = WELCOME_TRIGGER_DELAY_MS;
  #copyTimer;
  #scrollFrame;
  #previousFocus;
  #pageObserver;
  #connectedAt = performance.now();
  #mode = 'welcome';
  #detectedCountryCode = '';
  #detectedCountryName = '';
  #internationalOpenedAt = 0;
  #internationalLastSubmittedAt = 0;
  #readyToOpen = false;
  #submitting = false;
  #succeeded = false;
  #teaserAvailable = false;
  #teaserViewTracked = false;
  #hasVisibleOpen = false;
  #openTrigger = '';
  #eligibleTracked = false;
  #remoteEnrollmentPending = false;
  #privacyTimer;
  #experimentEndTimer;
  #analyticsRequests = new Set();
  #analyticsSessionId = '';
  /** @type {Promise<unknown> | null} */
  #discountApplyPromise = null;
  /** @type {WeakMap<HTMLFormElement, Promise<void>>} */
  #shopifyCaptchaReady = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    const inheritedCloseDialog = this.closeDialog;
    this.closeDialog = async () => {
      if (shouldBlockPopupClose(this.#submitting)) return;
      return inheritedCloseDialog();
    };

    this.#connectedAt = performance.now();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#initializeAnalyticsPrivacy(signal);
    this.#syncExperimentDeadline();
    document.addEventListener('kokoruno-popup-plan-change', this.#syncExperimentDeadline, { signal });

    this.refs.teaserButton.addEventListener('click', this.#handleTeaserClick, { signal });
    this.refs.teaserCloseButton.addEventListener('click', this.#handleTeaserClose, { signal });
    this.refs.closeButton.addEventListener('click', this.#requestClose, { signal });
    this.refs.copyButton.addEventListener('click', this.#handleCopy, { signal });
    this.refs.welcomeSuccessContinueButton.addEventListener('click', this.#requestClose, { signal });
    this.refs.existingSuccessContinueButton.addEventListener('click', this.#requestClose, { signal });
    this.refs.continueButton.addEventListener('click', this.#requestClose, { signal });
    this.refs.successContinueButton.addEventListener('click', this.#requestClose, { signal });
    this.refs.emailInput.addEventListener('input', this.#clearWelcomeError, { signal });
    this.refs.internationalEmailInput.addEventListener('input', this.#clearInternationalError, { signal });
    this.refs.internationalEmailInput.addEventListener('focus', this.#warmInternationalCaptcha, { signal, once: true });
    const welcomeForm = this.querySelector('#KokorunoNewsletterPopupForm');
    const internationalForm = this.querySelector('#KokorunoInternationalWaitlistForm');
    this.#ownFormSubmission(welcomeForm, signal);
    this.#ownFormSubmission(internationalForm, signal);
    this.refs.dialog.addEventListener('cancel', this.#handleCancel, { signal });
    this.refs.dialog.addEventListener('keydown', this.#handleOverlayTab, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    window.addEventListener('resize', this.#syncTeaser, { signal });
    window.visualViewport?.addEventListener('resize', this.#syncOverlayViewport, { signal });
    window.visualViewport?.addEventListener('scroll', this.#syncOverlayViewport, { signal });

    void this.#initialize();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#privacyTimer);
    window.clearTimeout(this.#experimentEndTimer);
    this.#abortAnalyticsRequests();
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#copyTimer);
    window.cancelAnimationFrame(this.#scrollFrame);
    this.#triggerAbortController?.abort();
    this.#stopTeaserObserver();
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  #handleOverlayTab = (event) => {
    if (event.key !== 'Tab' || this.dataset.experimentVariant !== 'overlay') return;
    const controls = [...this.refs.dialog.querySelectorAll('button, input, a[href], [tabindex]')]
      .filter((element) => element.tabIndex >= 0 && !element.disabled && !element.closest('[hidden]') && element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first) return;
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  #syncOverlayViewport = () => {
    if (this.dataset.experimentVariant !== 'overlay') return;
    const viewport = window.visualViewport;
    if (!viewport) return;
    // Keyboard changes can shrink only the visual viewport on mobile Safari.
    // Pinch zoom retains native panning rather than reflowing the dialog.
    if (viewport.scale !== 1 || !Number.isFinite(viewport.height) || viewport.height <= 0) {
      this.style.removeProperty('--k-popup-visual-height');
      this.style.removeProperty('--k-popup-visual-offset');
      return;
    }
    this.style.setProperty('--k-popup-visual-height', `${viewport.height}px`);
    this.style.setProperty('--k-popup-visual-offset', `${Math.max(0, viewport.offsetTop)}px`);
  };

  #syncExperimentDeadline = () => {
    window.clearTimeout(this.#experimentEndTimer);
    if (popupExperiment.isPreview()) return;
    if (this.dataset.experimentEnabled !== 'true') {
      this.dataset.experimentVariant = this.#mode === 'welcome' ? popupExperiment.presentation(this.dataset.pageType) : 'control';
      return;
    }
    const end = popupExperiment.deadline();
    if (end === null || !Number.isFinite(end)) {
      if (this.dataset.experimentSource === 'remote') { popupExperiment.context(); this.dataset.experimentVariant = 'control'; }
      return;
    }
    const remaining = end - Date.now();
    if (remaining <= 0) {
      popupExperiment.context();
      if (this.dataset.experimentVariant === 'overlay') this.dataset.experimentVariant = 'control';
      return;
    }
    // Bound each timer to handle long plans, clock changes and resumed tabs.
    this.#experimentEndTimer = window.setTimeout(this.#syncExperimentDeadline, Math.min(remaining, 60_000));
  };

  get #isDesignMode() {
    return this.dataset.designMode === 'true';
  }

  get #isPreviewEnabled() {
    return this.dataset.preview === 'true';
  }

  get #isWelcomeEnabled() {
    return this.dataset.welcomeEnabled === 'true';
  }

  get #isInternationalEnabled() {
    return this.dataset.internationalEnabled === 'true';
  }

  get #storageKey() {
    return this.dataset.storageKey || 'kokoruno_welcome_popup_v1';
  }

  get #sessionKey() {
    return this.dataset.sessionKey || 'kokoruno_welcome_popup_seen_v1';
  }

  get #teaserSessionKey() {
    return this.dataset.teaserSessionKey || 'kokoruno_welcome_teaser_closed_v1';
  }

  get #internationalStorageKey() {
    return this.dataset.internationalStorageKey || 'kokoruno_international_waitlist_v1';
  }

  get #internationalSessionKey() {
    return this.dataset.internationalSessionKey || 'kokoruno_international_waitlist_seen_v1';
  }

  get #internationalSubmitKey() {
    return this.dataset.internationalSubmitKey || 'kokoruno_international_waitlist_submit_v1';
  }

  get #countryCacheKey() {
    return this.dataset.countryCacheKey || 'kokoruno_detected_country_v2';
  }

  get #klaviyoPublicApiKey() {
    return this.dataset.klaviyoPublicApiKey?.trim() || '';
  }

  get #klaviyoWaitlistListId() {
    return this.dataset.klaviyoWaitlistListId?.trim() || '';
  }

  get #klaviyoNewsletterListId() {
    return this.dataset.klaviyoNewsletterListId?.trim() || '';
  }

  get #profitOsAnalyticsEndpoint() {
    const endpoint = this.dataset.profitOsAnalyticsEndpoint?.trim();
    if (!endpoint) return '';

    try {
      const url = new URL(endpoint);
      return url.protocol === 'https:' ? url.toString() : '';
    } catch (error) {
      return '';
    }
  }

  get #analyticsSessionKey() {
    return this.dataset.analyticsSessionKey || 'kokoruno_popup_analytics_session_v1';
  }

  get #eligibleSessionKey() {
    return this.dataset.eligibleSessionKey || 'kokoruno_welcome_popup_eligible_v2';
  }

  get #storefrontLocale() {
    return this.dataset.storefrontLocale?.trim() || document.documentElement.lang || 'en';
  }

  get #normalizedLocale() {
    const locale = (this.#storefrontLocale || 'en').trim();
    if (/^zh-(?:cn|hans)/i.test(locale)) return 'zh-TW';
    return locale;
  }

  async #initialize() {
    // Only opted-in remote mode makes cart attribution-only. OFF/saved-theme
    // mode retains ordinary welcome and waitlist popup initialization.
    if (this.dataset.pageType === 'cart' && this.dataset.experimentSource === 'remote' &&
      this.dataset.experimentEnabled === 'true') return;
    const postedMode = this.#getPostedMode();
    const mode = postedMode || (await this.#resolveMode());
    if (!this.isConnected) return;

    this.#setMode(mode);

    const serverResult = this.querySelector(`[data-newsletter-popup-result="${mode}"]`);
    if (serverResult?.dataset.success === 'true') {
      this.#showSuccess(mode);
      this.#open({ immediate: true, focusSuccess: true });
      return;
    }

    const serverError = serverResult?.textContent?.trim();
    if (serverError) {
      this.#showError(serverError, mode);
      this.#open({ immediate: true });
      return;
    }

    this.#schedule();
  }

  #getPostedMode() {
    if (window.location.hash === '#KokorunoNewsletterPopupForm') {
      const result = this.querySelector('[data-newsletter-popup-result="welcome"]');
      const hasFeedback = result?.dataset.success === 'true' || Boolean(result?.textContent?.trim());
      return hasFeedback ? 'welcome' : '';
    }

    if (window.location.hash === '#KokorunoInternationalWaitlistForm') {
      const result = this.querySelector('[data-newsletter-popup-result="international"]');
      const hasFeedback = result?.dataset.success === 'true' || Boolean(result?.textContent?.trim());
      return hasFeedback ? 'international' : '';
    }

    return '';
  }

  async #resolveMode() {
    if (this.#isDesignMode && this.#isPreviewEnabled) {
      return this.dataset.previewMode === 'international' ? 'international' : 'welcome';
    }

    const availableCountries = this.#readAvailableCountries();
    if (!availableCountries.length) return 'welcome';

    const detectedCountry = await this.#detectCountry();
    if (!detectedCountry) return 'welcome';

    this.#detectedCountryCode = detectedCountry.code;
    this.#detectedCountryName = detectedCountry.name;

    const localeGuard = window.KokorunoMarketLocaleGuard;
    if (localeGuard?.ensureEnglishForCountry?.(detectedCountry) || localeGuard?.switchingToEnglish) {
      return 'none';
    }

    return availableCountries.includes(this.#detectedCountryCode) ? 'welcome' : 'international';
  }

  #readAvailableCountries() {
    try {
      const source = this.querySelector('[data-newsletter-popup-available-countries]')?.textContent || '[]';
      const countries = JSON.parse(source);
      if (!Array.isArray(countries)) return [];

      return countries.map((country) => String(country).toUpperCase()).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  async #detectCountry() {
    const localeGuard = window.KokorunoMarketLocaleGuard;
    if (localeGuard?.ready) {
      await localeGuard.ready;
      if (localeGuard.country) return localeGuard.country;
    }

    const cachedCountry = this.#readCachedCountry();
    if (cachedCountry) return cachedCountry;

    const requestController = new AbortController();
    const requestTimer = window.setTimeout(() => requestController.abort(), COUNTRY_REQUEST_TIMEOUT);
    this.#abortController.signal.addEventListener('abort', () => requestController.abort(), { once: true });

    try {
      const root = window.Shopify?.routes?.root || '/';
      const storefrontCountry = String(window.Shopify?.country || '').toUpperCase();
      const excludeCountry = /^[A-Z]{2}$/.test(storefrontCountry)
        ? `&country[exclude]=${encodeURIComponent(storefrontCountry)}`
        : '';
      const response = await fetch(
        `${root}browsing_context_suggestions.json?country[enabled]=true${excludeCountry}`,
        {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: requestController.signal,
        }
      );

      if (!response.ok) return null;

      const payload = await response.json();
      const code = String(payload?.detected_values?.country?.handle || '').toUpperCase();
      const name = String(payload?.detected_values?.country?.name || '').trim();
      if (!/^[A-Z]{2}$/.test(code) || !name) return null;

      const country = { code, name, locale: this.#storefrontLocale };
      this.#writeSessionValue(this.#countryCacheKey, JSON.stringify(country));
      return country;
    } catch (error) {
      return null;
    } finally {
      window.clearTimeout(requestTimer);
    }
  }

  #readCachedCountry() {
    try {
      const country = JSON.parse(this.#readSessionValue(this.#countryCacheKey));
      const code = String(country?.code || '').toUpperCase();
      const name = String(country?.name || '').trim();
      const locale = String(country?.locale || '').trim();
      return /^[A-Z]{2}$/.test(code) && name && locale === this.#storefrontLocale ? { code, name } : null;
    } catch (error) {
      return null;
    }
  }

  #setMode(mode) {
    this.#mode = mode;
    this.dataset.mode = mode;
    this.#teaserAvailable = false;
    this.refs.teaserShell.hidden = true;
    this.refs.teaserButton.hidden = true;
    this.refs.welcomeShell.hidden = mode !== 'welcome';
    this.refs.internationalShell.hidden = mode !== 'international';

    if (mode === 'international') {
      this.#setInternationalCountryCopy();
    }

    if (mode !== 'none') {
      this.#setDialogLabels({ success: false });
    }
  }

  #schedule() {
    if (this.#isDesignMode && !this.#isPreviewEnabled) return;
    if (this.#mode === 'none') return;

    if (this.#mode === 'international') {
      if (!this.#isDesignMode && (!this.#isInternationalEnabled || this.#isInternationalSuppressed())) return;
      this.#scheduleInternationalTimer();
      return;
    }

    const welcomeSuppressed = this.#isWelcomeSuppressed();
    const storedSubscriber = this.#hasSubscribed();
    const knownSubscriber = this.#isKnownWelcomeSubscriber();
    const welcomeEligible =
      this.#isDesignMode ||
      isWelcomePopupEligible({
        enabled: this.#isWelcomeEnabled,
        knownSubscriber,
        storedSubscriber,
        suppressed: welcomeSuppressed,
      });

    if (!welcomeEligible) {
      if (welcomeSuppressed && !storedSubscriber && !knownSubscriber && !this.#isTeaserSuppressed()) {
        this.#teaserAvailable = true;
        this.#syncTeaser();
      }
      return;
    }

    if (this.#isDesignMode) {
      this.#openTimer = window.setTimeout(() => this.#fireWelcomeTrigger('timer'), 100);
      return;
    }

    const start = (assignment) => {
      if (this.#abortController.signal.aborted || !this.isConnected) return;
      if (assignment) this.#track('newsletter_popup_assigned');
      this.#trackEligibleOnce();
      this.#startWelcomeTriggers();
    };
    if (this.dataset.experimentSource === 'remote') {
      // Remote decisions run alongside the unchanged active timer/scroll/exit
      // triggers. A slow config read must not defer signup or popup A.
      this.#remoteEnrollmentPending = true;
      start(null);
      this.#tryRemoteEnrollment();
      return;
    }
    const enrollment = popupExperiment.enrollLocked(this.dataset.pageType);
    if (enrollment?.then) void enrollment.then(start, () => start(null));
    else start(enrollment);
  }

  #tryRemoteEnrollment() {
    if (!this.#remoteEnrollmentPending || this.#abortController.signal.aborted || !this.isConnected) return;
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
    } catch { return; }
    this.#remoteEnrollmentPending = false;
    const enrollment = popupExperiment.enrollLocked(this.dataset.pageType);
    if (enrollment?.then) void enrollment.then(assignment => {
      if (assignment && this.isConnected && !this.#abortController.signal.aborted) this.#track('newsletter_popup_assigned');
    }, () => {});
  }

  #scheduleInternationalTimer() {
    this.#warmInternationalCaptcha();
    const configuredDelay = Number(this.dataset.internationalDelayMs);
    const elapsed = performance.now() - this.#connectedAt;
    const delay = this.#isDesignMode ? 100 : Math.max(0, (configuredDelay || 0) - elapsed);

    this.#openTimer = window.setTimeout(() => {
      this.#readyToOpen = true;
      this.#attemptOpen();
    }, delay);
  }

  #trackEligibleOnce() {
    if (this.#eligibleTracked) return;
    // This opportunity happened now. Never replay it when consent arrives later.
    this.#eligibleTracked = true;
    if (this.#isDesignMode || !this.#profitOsAnalyticsEndpoint || !this.#analyticsAllowed()) return;

    runOncePerSession({
      key: this.#eligibleSessionKey,
      read: (key) => this.#readSessionValue(key),
      run: () => this.#track('newsletter_popup_eligible'),
      write: (key, value) => this.#writeSessionValue(key, value),
    });
  }

  #startWelcomeTriggers() {
    this.#triggerAbortController?.abort();
    this.#triggerAbortController = new AbortController();
    const { signal } = this.#triggerAbortController;
    const { delayMs } = getWelcomeTriggerSettings(this.dataset.pageType, Number(this.dataset.delayMs));
    this.#remainingActiveDelay = delayMs;

    window.addEventListener('scroll', this.#handleWelcomeScroll, { passive: true, signal });

    if (this.#supportsExitIntent()) {
      document.addEventListener('mouseout', this.#handleExitIntent, { signal });
    }

    this.#resumeActiveTimer();
    this.#handleWelcomeScroll();
  }

  #resumeActiveTimer() {
    if (document.visibilityState === 'hidden' || this.#readyToOpen || this.refs.dialog.open) return;

    if (this.#remainingActiveDelay <= 0) {
      this.#fireWelcomeTrigger('timer');
      return;
    }

    window.clearTimeout(this.#openTimer);
    this.#activeTimerStartedAt = performance.now();
    this.#openTimer = window.setTimeout(() => this.#fireWelcomeTrigger('timer'), this.#remainingActiveDelay);
  }

  #pauseActiveTimer() {
    if (!this.#activeTimerStartedAt) return;

    this.#remainingActiveDelay = Math.max(
      0,
      this.#remainingActiveDelay - (performance.now() - this.#activeTimerStartedAt)
    );
    this.#activeTimerStartedAt = 0;
    window.clearTimeout(this.#openTimer);
  }

  #welcomeScrollRoot() {
    return document.querySelector('[data-welcome-scroll-root]');
  }

  #handleWelcomeScroll = () => {
    if (this.#scrollFrame) return;

    this.#scrollFrame = window.requestAnimationFrame(() => {
      this.#scrollFrame = undefined;
      if (this.#mode !== 'welcome' || this.#readyToOpen || this.refs.dialog.open) return;

      const { scrollThreshold } = getWelcomeTriggerSettings(this.dataset.pageType);
      const articleRoot =
        isWelcomeArticlePage(this.dataset.pageType) && this.#welcomeScrollRoot();
      const contentTop = articleRoot
        ? articleRoot.getBoundingClientRect().top + window.scrollY
        : undefined;
      const contentHeight = articleRoot ? articleRoot.offsetHeight : undefined;
      const reachedThreshold = hasReachedWelcomeScrollDepth({
        scrollHeight: document.documentElement.scrollHeight,
        scrollY: window.scrollY,
        viewportHeight: window.innerHeight,
        threshold: scrollThreshold,
        contentTop,
        contentHeight,
      });
      if (reachedThreshold) this.#fireWelcomeTrigger('scroll');
    });
  };

  #supportsExitIntent() {
    return supportsWelcomeExitIntent({
      finePointer: window.matchMedia('(pointer: fine)').matches,
      hover: window.matchMedia('(hover: hover)').matches,
      viewportWidth: window.innerWidth,
    });
  }

  #handleExitIntent = (event) => {
    if (event.relatedTarget || event.clientY > 0 || !this.#supportsExitIntent()) return;
    this.#fireWelcomeTrigger('exit_intent');
  };

  #fireWelcomeTrigger(trigger) {
    if (this.#mode !== 'welcome' || this.#readyToOpen || this.refs.dialog.open) return;
    if (!this.#isDesignMode && (this.#isWelcomeSuppressed() || this.#isKnownWelcomeSubscriber())) return;

    this.#pauseActiveTimer();
    this.#triggerAbortController?.abort();
    this.#readyToOpen = true;
    this.#openTrigger = trigger;
    this.#attemptOpen();
  }

  #attemptOpen = () => {
    const competingOverlay = this.#hasCompetingOverlay();
    const suppressed =
      !this.#isDesignMode &&
      (this.#mode === 'international'
        ? this.#isInternationalSuppressed()
        : this.#isWelcomeSuppressed() || this.#isKnownWelcomeSubscriber());
    const decision = getPopupOpeningDecision({
      competingOverlay,
      suppressed,
      visible: document.visibilityState !== 'hidden',
    });

    if (decision === 'blocked') {
      this.#readyToOpen = false;
      return;
    }

    if (decision === 'wait') {
      if (!competingOverlay) return;
      this.#openTimer = window.setTimeout(this.#attemptOpen, RETRY_DELAY);
      return;
    }

    this.#open();
  };

  #open({ immediate = false, focusSuccess = false, trigger = '' } = {}) {
    if (this.refs.dialog.open) return;

    if (!immediate && !this.#isDesignMode) {
      if (this.#mode === 'international' && this.#isInternationalSuppressed()) return;
      if (this.#mode === 'welcome' && (this.#isWelcomeSuppressed() || this.#isKnownWelcomeSubscriber())) return;
    }

    this.#readyToOpen = false;
    this.#pauseActiveTimer();
    this.#triggerAbortController?.abort();
    window.clearTimeout(this.#openTimer);
    if (trigger) this.#openTrigger = trigger;
    this.#previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;

    if (!this.#isDesignMode) {
      const sessionKey = this.#mode === 'international' ? this.#internationalSessionKey : this.#sessionKey;
      this.#writeSessionValue(sessionKey, '1');
    }

    if (this.#mode === 'international') this.#internationalOpenedAt = Date.now();

    this.dataset.experimentVariant = this.#mode === 'welcome' ? popupExperiment.presentation(this.dataset.pageType) : 'control';
    this.#syncOverlayViewport();
    popupExperiment.opened();
    this.showDialog();
    this.#syncTeaser();
    this.#warmVisibleCaptcha();

    window.requestAnimationFrame(() => {
      if (!this.isConnected || !this.refs.dialog.open) return;

      this.#hasVisibleOpen = true;
      if (this.#mode === 'welcome' && !this.refs.initialState.hidden && document.visibilityState !== 'hidden' && popupExperiment.context()) {
        this.#track('newsletter_popup_exposure', { trigger: this.#openTrigger });
      }
      this.#track('newsletter_popup_view', { trigger: this.#openTrigger });
      const target = this.#getFocusTarget(focusSuccess);
      target.focus({ preventScroll: true });
    });
  }

  #getFocusTarget(focusSuccess) {
    if (this.#mode === 'international') {
      return focusSuccess ? this.refs.internationalSuccessHeading : this.refs.internationalEmailInput;
    }

    return focusSuccess
      ? this.dataset.welcomeResult === 'existing'
        ? this.refs.existingSuccessHeading
        : this.refs.successHeading
      : this.refs.emailInput;
  }

  #setInternationalCountryCopy() {
    const countryName = this.#detectedCountryName || this.dataset.previewCountryName?.trim();
    if (!countryName) return;

    for (const heading of [this.refs.internationalHeading, this.refs.internationalSuccessHeading]) {
      const template = heading.dataset.countryTemplate;
      if (template) heading.textContent = template.replace('__COUNTRY__', countryName);
    }
  }

  #handleVisibilityChange = () => {
    this.#syncExperimentDeadline();
    if (document.visibilityState === "visible") popupExperiment.resume();
    if (this.#mode === 'welcome' && this.#triggerAbortController && !this.#readyToOpen) {
      if (document.visibilityState === 'hidden') this.#pauseActiveTimer();
      else this.#resumeActiveTimer();
    }

    if (!this.#readyToOpen || document.visibilityState !== 'visible' || this.refs.dialog.open) return;
    if (this.#isDesignMode && !this.#isPreviewEnabled) return;

    if (!this.#isDesignMode) {
      if (this.#mode === 'international' && this.#isInternationalSuppressed()) return;
      if (this.#mode === 'welcome' && (this.#isWelcomeSuppressed() || this.#isKnownWelcomeSubscriber())) return;
    }

    this.#attemptOpen();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    if (shouldBlockPopupClose(this.#submitting)) return;
    this.#requestClose();
  };

  #requestClose = () => {
    if (shouldBlockPopupClose(this.#submitting)) return;
    if (this.refs.dialog.classList.contains('dialog-closing')) return;
    this.closeDialog();
  };

  #handleTeaserClick = () => {
    if (this.#mode !== 'welcome' || this.#hasCompetingOverlay()) return;
    this.#track('newsletter_popup_teaser_open', { trigger: 'manual_teaser' });
    this.#open({ immediate: true, trigger: 'manual_teaser' });
  };

  #handleTeaserClose = () => {
    this.#track('newsletter_popup_teaser_dismiss');
    this.#teaserAvailable = false;

    if (!this.#isDesignMode) {
      this.#writeSessionValue(this.#teaserSessionKey, '1');
    }

    this.refs.teaserCloseButton.blur();
    this.#syncTeaser();
  };

  #handleDialogClose = () => {
    if (this.#hasVisibleOpen && !this.#succeeded) {
      this.#track('newsletter_popup_dismiss');
    }
    this.#hasVisibleOpen = false;

    if (!this.#isDesignMode && !this.#succeeded) {
      if (this.#mode === 'international') {
        this.#writeInternationalState({
          dismissedAt: Date.now(),
          countryCode: this.#detectedCountryCode,
          countryName: this.#detectedCountryName,
        });
      } else {
        this.#writeWelcomeState({ dismissedAt: Date.now() });
      }
    }

    if (this.#mode === 'welcome' && !this.#succeeded) {
      this.#teaserAvailable = !this.#isTeaserSuppressed();
      this.#syncTeaser();
    }

    if (this.#previousFocus?.isConnected) {
      this.#previousFocus.focus({ preventScroll: true });
    }
  };

  #ownFormSubmission(form, signal) {
    if (!(form instanceof HTMLFormElement)) return;

    form.dataset.shopifyAction = form.getAttribute('action') || form.action;
    form.setAttribute('method', 'dialog');
    form.submit = () => {};

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#submitForm(form);
      },
      { signal }
    );
  }

  #submitForm(form) {
    const mode = form.id === 'KokorunoInternationalWaitlistForm' ? 'international' : 'welcome';

    if (this.#submitting) return;

    const input = mode === 'international' ? this.refs.internationalEmailInput : this.refs.emailInput;
    const email = input.value.trim();

    if (!email) {
      this.#showError(this.dataset.missingError || 'This field is required', mode);
      input.focus();
      return;
    }

    if (!input.validity.valid || !isLikelyEmail(email)) {
      this.#showError(this.dataset.invalidError || 'Enter a valid email address.', mode);
      input.focus();
      return;
    }

    if (mode === 'international') {
      const now = Date.now();
      const openedAt = this.#internationalOpenedAt || now;
      const storedLastSubmitAt = Number(this.#readSessionValue(this.#internationalSubmitKey)) || 0;
      const lastSubmitAt = Math.max(this.#internationalLastSubmittedAt, storedLastSubmitAt);
      if (
        now - openedAt < INTERNATIONAL_MIN_DWELL ||
        (lastSubmitAt > 0 && now - lastSubmitAt < INTERNATIONAL_SUBMIT_COOLDOWN)
      ) {
        this.#showError(this.dataset.internationalWaitError || this.dataset.genericError, mode);
        input.focus();
        return;
      }

      this.#internationalLastSubmittedAt = now;
      this.#writeSessionValue(this.#internationalSubmitKey, String(now));
    }

    this.#clearError(mode);
    this.#track('newsletter_popup_submit_attempt', { popup_mode: mode });
    this.#setSubmitting(true, mode);

    if (mode === 'international') {
      void this.#subscribeInternationalWaitlist(form, email);
      return;
    }

    void this.#subscribeWelcome(form, email);
  }

  async #subscribeWelcome(form, email) {
    try {
      let offerEligible = true;

      await submitWelcomeShopifyFirst({
        submitToShopify: async () => {
          offerEligible = await this.#submitWelcomeSignup(email);
        },
        syncToKlaviyo: this.#isWelcomeKlaviyoConfigured()
          ? () =>
              this.#createKlaviyoSubscription({
                email,
                listId: this.#klaviyoNewsletterListId,
                customSource: 'Kokoruno welcome popup',
                properties: {
                  signup_source: 'welcome_popup',
                  signup_storefront_locale: this.#storefrontLocale,
                },
              })
          : undefined,
      });

      if (!this.isConnected) return;
      this.#track('newsletter_popup_submit_success', { popup_mode: 'welcome', trigger: this.#openTrigger });
      this.#showSuccess('welcome', { offerEligible });
      if (shouldReopenAfterWelcomeSignupSuccess(this.refs.dialog.open)) {
        this.#open({ immediate: true, focusSuccess: true });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError' && error.message === 'aborted') return;
      if (!this.isConnected) return;

      this.#showError(this.#userFacingSignupError(error, 'welcome'), 'welcome');
      this.#track('newsletter_popup_submit_error', { popup_mode: 'welcome', trigger: this.#openTrigger });
    } finally {
      if (this.isConnected) this.#setSubmitting(false, 'welcome');
    }
  }

  async #submitWelcomeSignup(email) {
    const endpoint = newsletterSignupEndpoint(this.#profitOsAnalyticsEndpoint);
    if (!endpoint) throw new Error('Welcome signup is not configured');

    return postWelcomeSignup({
      body: welcomeSignupRequestBody({
        email,
        honeypotValue: this.refs.welcomeHoneypot.value,
        locale: this.#storefrontLocale,
      }),
      endpoint,
      externalSignal: this.#abortController.signal,
      timeoutMs: WELCOME_SIGNUP_TIMEOUT_MS,
    });
  }

  async #postShopifyCustomerForm(form, { allowCaptchaRetry = true } = {}) {
    await this.#prepareShopifyCaptcha(form);
    const response = await fetch(this.#shopifyFormAction(form), {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
    });
    const html = await response.text();

    if (allowCaptchaRetry && this.#isMissingCaptchaResponse(response, html)) {
      this.#forgetShopifyCaptcha(form);
      return this.#postShopifyCustomerForm(form, { allowCaptchaRetry: false });
    }

    return { html, response };
  }

  async #subscribeInternationalWaitlist(form, email) {
    try {
      if (this.#isInternationalKlaviyoConfigured()) {
        await this.#createKlaviyoSubscription({
          email,
          listId: this.#klaviyoWaitlistListId,
          customSource: 'Kokoruno international waitlist',
          properties: {
            signup_source: 'international_waitlist',
            waitlist_country_code: this.#detectedCountryCode,
            waitlist_country_name: this.#detectedCountryName,
            waitlist_storefront_locale: this.#storefrontLocale,
          },
        });
        if (!this.isConnected) return;
        void this.#submitInternationalToShopify(form).catch(() => {
          // Klaviyo already recorded consent; Shopify tags are best-effort.
        });
      } else {
        await this.#submitInternationalToShopify(form);
      }

      if (!this.isConnected) return;
      this.#track('newsletter_popup_submit_success', { popup_mode: 'international' });
      this.#showSuccess('international');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError' && error.message === 'aborted') return;
      if (!this.isConnected) return;

      this.#showError(
        this.dataset.internationalApiError || 'We could not join the waitlist. Please try again.',
        'international'
      );
      this.#track('newsletter_popup_submit_error', { popup_mode: 'international' });
    } finally {
      if (this.isConnected) this.#setSubmitting(false, 'international');
    }
  }

  async #submitInternationalToShopify(form) {
    const tagsInput = form.querySelector('input[name="contact[tags]"]');
    if (tagsInput) {
      const countryCode = this.#detectedCountryCode?.toUpperCase();
      const countryTag = countryCode ? `waitlist-country-${countryCode}` : '';
      const localeTag = `locale-${this.#normalizedLocale}`;
      tagsInput.value = ['international-waitlist', countryTag, localeTag].filter(Boolean).join(',');
    }

    const { response } = await this.#postShopifyCustomerForm(form);
    if (!response.ok) throw new Error('Shopify waitlist signup was not accepted');
  }

  #shopifyFormAction(form) {
    return form.dataset.shopifyAction || form.getAttribute('action') || form.action;
  }

  #readShopifyContactFailure(html) {
    return html.match(/<title>([^<]+)<\/title>/i)?.[1]?.trim() || '';
  }

  #isMissingCaptchaResponse(response, html) {
    return response.status === 400 && /captcha/i.test(this.#readShopifyContactFailure(html) || html);
  }

  #userFacingSignupError(error, mode) {
    const message = error instanceof Error ? error.message.trim() : '';
    const generic =
      mode === 'international'
        ? this.dataset.internationalApiError || 'We could not join the waitlist. Please try again.'
        : this.dataset.genericError || 'Something went wrong. Please try again.';

    if (!message || /captcha|not accepted|not confirmed|not ready|not configured|timed out|offline/i.test(message)) return generic;
    return message;
  }

  #prepareShopifyCaptcha(form) {
    const pending = this.#shopifyCaptchaReady.get(form);
    if (pending) return pending;

    const wait = this.#waitForShopifyCaptcha(form).catch((error) => {
      this.#shopifyCaptchaReady.delete(form);
      throw error;
    });
    this.#shopifyCaptchaReady.set(form, wait);
    return wait;
  }

  #forgetShopifyCaptcha(form) {
    this.#shopifyCaptchaReady.delete(form);
    const tokenField = form.querySelector('[name="h-captcha-response"]');
    if (tokenField) tokenField.value = '';
  }

  #warmVisibleCaptcha() {
    if (this.#mode === 'international') this.#warmInternationalCaptcha();
  }

  #warmInternationalCaptcha = () => {
    const form = this.querySelector('#KokorunoInternationalWaitlistForm');
    if (form instanceof HTMLFormElement) void this.#prepareShopifyCaptcha(form).catch(() => {});
  };

  #waitForShopifyCaptcha(form) {
    const hasToken = () => Boolean(form.querySelector('[name="h-captcha-response"]')?.value?.trim());
    if (hasToken()) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let poll;
      let calledProtect = false;
      let observingField = null;

      const cleanup = () => {
        window.clearTimeout(timer);
        window.clearInterval(poll);
        observingField?.removeEventListener('input', onToken);
        observer.disconnect();
      };

      const timer = window.setTimeout(() => {
        cleanup();
        reject(new Error('Shopify CAPTCHA was not ready'));
      }, SHOPIFY_CAPTCHA_TIMEOUT);

      const onToken = () => {
        if (!hasToken()) return;
        cleanup();
        resolve();
      };

      const observer = new MutationObserver(onToken);
      observer.observe(form, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
      });

      const tick = () => {
        const tokenField = form.querySelector('[name="h-captcha-response"]');
        if (tokenField && tokenField !== observingField) {
          observingField?.removeEventListener('input', onToken);
          observingField = tokenField;
          tokenField.addEventListener('input', onToken);
        }

        onToken();

        const protect = window.Shopify?.captcha?.protect;
        if (calledProtect || typeof protect !== 'function') return;

        calledProtect = true;
        try {
          protect(form, onToken);
        } catch (error) {
          cleanup();
          reject(error);
        }
      };

      poll = window.setInterval(tick, 50);
      tick();
    });
  }

  async #createKlaviyoSubscription({ email, listId, customSource, properties }) {
    const requestController = new AbortController();
    let requestTimedOut = false;
    const requestTimer = window.setTimeout(() => {
      requestTimedOut = true;
      requestController.abort();
    }, INTERNATIONAL_REQUEST_TIMEOUT);
    this.#abortController.signal.addEventListener('abort', () => requestController.abort(), { once: true });

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.#klaviyoPublicApiKey)}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: '2026-07-15',
          },
          body: JSON.stringify(this.#buildKlaviyoSubscription({ email, listId, customSource, properties })),
          signal: requestController.signal,
        }
      );

      if (response.status !== 202) throw new Error('Klaviyo subscription was not accepted');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError' && !requestTimedOut) {
        throw new DOMException('aborted', 'AbortError');
      }
      throw error;
    } finally {
      window.clearTimeout(requestTimer);
    }
  }

  #buildKlaviyoSubscription({ email, listId, customSource, properties }) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                locale: this.#storefrontLocale,
                properties,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };
  }

  #setSubmitting(isSubmitting, mode) {
    this.#submitting = isSubmitting;
    const button = mode === 'international' ? this.refs.internationalSubmitButton : this.refs.submitButton;
    button.disabled = isSubmitting;
    button.setAttribute('aria-busy', String(isSubmitting));
    button.toggleAttribute('data-loading', isSubmitting);
    this.refs.closeButton.disabled = isSubmitting;
    this.refs.closeButton.setAttribute('aria-disabled', String(isSubmitting));
  }

  #showError(message, mode) {
    const isInternational = mode === 'international';
    const errorText = isInternational ? this.refs.internationalErrorText : this.refs.errorText;
    const errorMessage = isInternational ? this.refs.internationalErrorMessage : this.refs.errorMessage;
    const input = isInternational ? this.refs.internationalEmailInput : this.refs.emailInput;

    errorText.textContent = message;
    errorMessage.dataset.visible = 'true';
    input.setAttribute('aria-invalid', 'true');
  }

  #clearError(mode) {
    const isInternational = mode === 'international';
    const errorText = isInternational ? this.refs.internationalErrorText : this.refs.errorText;
    const errorMessage = isInternational ? this.refs.internationalErrorMessage : this.refs.errorMessage;
    const input = isInternational ? this.refs.internationalEmailInput : this.refs.emailInput;

    errorText.textContent = '';
    errorMessage.dataset.visible = 'false';
    input.removeAttribute('aria-invalid');
  }

  #clearWelcomeError = () => this.#clearError('welcome');

  #clearInternationalError = () => this.#clearError('international');

  #showSuccess(mode, { offerEligible = true } = {}) {
    this.#succeeded = true;
    this.#teaserAvailable = false;
    this.#syncTeaser();

    if (mode === 'international') {
      this.refs.internationalInitialState.hidden = true;
      this.refs.internationalSuccessState.hidden = false;
      this.#writeInternationalState({
        joined: true,
        joinedAt: Date.now(),
        countryCode: this.#detectedCountryCode,
        countryName: this.#detectedCountryName,
      });
    } else {
      this.refs.initialState.hidden = true;
      this.refs.successState.hidden = false;
      this.refs.welcomeOfferSuccess.hidden = !offerEligible;
      this.refs.welcomeExistingSuccess.hidden = offerEligible;
      this.dataset.welcomeResult = offerEligible ? 'offer' : 'existing';
      this.#writeWelcomeState({ subscribed: true, subscribedAt: Date.now() });
      if (offerEligible) {
        void this.#applyWelcomeDiscount()
          .then((result) => {
            if (!result) return;
            this.#showCopyFeedback(this.dataset.appliedMessage || '10% off is on your cart');
          })
          .catch(() => {
            // The code remains visible if Shopify cannot attach it yet.
          });
      }
    }

    this.#setDialogLabels({ offerEligible, success: true });

    if (this.refs.dialog.open) {
      this.#getFocusTarget(true).focus({ preventScroll: true });
    }
  }

  #setDialogLabels({ offerEligible = this.dataset.welcomeResult !== 'existing', success }) {
    const labels =
      this.#mode === 'international'
        ? success
          ? ['KokorunoInternationalPopupSuccessHeading', 'KokorunoInternationalPopupSuccessBody']
          : ['KokorunoInternationalPopupHeading', 'KokorunoInternationalPopupBody']
        : success
          ? offerEligible
            ? ['KokorunoNewsletterPopupSuccessHeading', 'KokorunoNewsletterPopupSuccessBody']
            : ['KokorunoNewsletterPopupExistingHeading', 'KokorunoNewsletterPopupExistingBody']
          : ['KokorunoNewsletterPopupHeading', 'KokorunoNewsletterPopupBody'];

    this.refs.dialog.setAttribute('aria-labelledby', labels[0]);
    this.refs.dialog.setAttribute('aria-describedby', labels[1]);
  }

  #handleCopy = async () => {
    const code = this.refs.couponCode.textContent?.trim();
    if (!code) return;

    this.refs.copyButton.disabled = true;
    let copied = false;
    let applied = false;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        this.#copyWithFallback(code);
      }
      copied = true;
    } catch (error) {
      copied = false;
    }

    try {
      await this.#applyWelcomeDiscount();
      applied = true;
    } catch (error) {
      applied = false;
    }

    this.refs.copyButton.disabled = false;

    if (copied && applied) {
      this.#showCopyFeedback(
        this.dataset.copiedAppliedMessage || this.dataset.copiedMessage || 'Copied — applied to your cart'
      );
      return;
    }

    if (applied) {
      this.#showCopyFeedback(this.dataset.appliedMessage || '10% off is on your cart');
      return;
    }

    if (copied) {
      this.#showCopyFeedback(this.dataset.copiedMessage || 'Coupon code copied');
      return;
    }

    this.#showCopyFeedback(this.dataset.copyError || 'Copy failed. Select and copy the code manually.');
  };

  async #applyWelcomeDiscount() {
    if (this.#isDesignMode) return null;

    const code = this.refs.couponCode.textContent?.trim();
    if (!code) return null;

    if (this.#discountApplyPromise) return this.#discountApplyPromise;

    this.#discountApplyPromise = applyDiscountCode(code, { source: 'newsletter-popup' }).finally(() => {
      this.#discountApplyPromise = null;
    });

    return this.#discountApplyPromise;
  }

  #copyWithFallback(code) {
    const field = document.createElement('textarea');
    field.value = code;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    this.refs.dialog.appendChild(field);
    field.select();

    const copied = document.execCommand('copy');
    field.remove();
    this.refs.copyButton.focus({ preventScroll: true });

    if (!copied) throw new Error('Copy command failed');
  }

  #showCopyFeedback(message) {
    window.clearTimeout(this.#copyTimer);
    this.refs.copyFeedback.textContent = message;
    this.refs.copyFeedback.dataset.visible = 'true';
    this.#copyTimer = window.setTimeout(() => {
      this.refs.copyFeedback.textContent = '';
      this.refs.copyFeedback.dataset.visible = 'false';
    }, 4000);
  }

  #isWelcomeSuppressed() {
    if (this.#readSessionValue(this.#sessionKey) === '1') return true;

    const state = this.#readState(this.#storageKey);
    if (state.subscribed) return true;

    const dismissDays = Math.max(1, Number(this.dataset.dismissDays) || 1);
    return typeof state.dismissedAt === 'number' && Date.now() - state.dismissedAt < dismissDays * DAY;
  }

  #isTeaserSuppressed() {
    return this.#readSessionValue(this.#teaserSessionKey) === '1';
  }

  #isInternationalSuppressed() {
    if (this.#readSessionValue(this.#internationalSessionKey) === '1') return true;

    const state = this.#readState(this.#internationalStorageKey);
    if (state.joined) return true;

    const dismissDays = Math.max(1, Number(this.dataset.internationalDismissDays) || 1);
    return typeof state.dismissedAt === 'number' && Date.now() - state.dismissedAt < dismissDays * DAY;
  }

  #isInternationalKlaviyoConfigured() {
    return Boolean(this.#klaviyoPublicApiKey && this.#klaviyoWaitlistListId);
  }

  #isWelcomeKlaviyoConfigured() {
    return Boolean(this.#klaviyoPublicApiKey && this.#klaviyoNewsletterListId);
  }

  #hasSubscribed() {
    return this.#readState(this.#storageKey).subscribed === true;
  }

  #isKnownWelcomeSubscriber() {
    return this.dataset.welcomeSubscribed === 'true' || this.dataset.welcomeExistingCustomer === 'true';
  }

  #hasCompetingOverlay() {
    const overlays = document.querySelectorAll(
      'dialog[open]:not(.k-newsletter-popup__dialog), details[open][scroll-lock], [role="dialog"]'
    );

    return [...overlays].some((overlay) => {
      if (!(overlay instanceof HTMLElement) || overlay.hidden) return false;

      const style = window.getComputedStyle(overlay);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;

      const bounds = overlay.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0;
    });
  }

  #startTeaserObserver() {
    if (this.#pageObserver) return;

    this.#pageObserver = new MutationObserver(this.#syncTeaser);
    this.#pageObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['open', 'data-stuck', 'data-variant-available'],
      childList: true,
      subtree: true,
    });
  }

  #stopTeaserObserver() {
    this.#pageObserver?.disconnect();
    this.#pageObserver = undefined;
  }

  #syncTeaser = () => {
    const teaserCanBeEligible =
      this.#mode === 'welcome' && this.#teaserAvailable && !this.#succeeded && !this.#isTeaserSuppressed();
    if (teaserCanBeEligible) this.#startTeaserObserver();
    else this.#stopTeaserObserver();

    const shouldShow =
      teaserCanBeEligible &&
      !this.refs.dialog.open &&
      !this.#hasCompetingOverlay();

    this.refs.teaserShell.hidden = !shouldShow;
    this.refs.teaserButton.hidden = !shouldShow;

    if (!shouldShow) {
      this.style.removeProperty('--k-newsletter-popup-teaser-bottom');
      return;
    }

    if (!this.#teaserViewTracked) {
      this.#teaserViewTracked = true;
      this.#track('newsletter_popup_teaser_view');
    }

    const stickyBar = document.querySelector(
      "sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__bar[data-stuck='true']"
    );

    if (!(stickyBar instanceof HTMLElement)) {
      this.style.removeProperty('--k-newsletter-popup-teaser-bottom');
      return;
    }

    const stickyHeight = Math.ceil(stickyBar.getBoundingClientRect().height);
    const gap = window.matchMedia('(min-width: 750px)').matches ? 24 : 12;
    this.style.setProperty('--k-newsletter-popup-teaser-bottom', `${stickyHeight + gap}px`);
  };

  #initializeAnalyticsPrivacy(signal) {
    if (this.#isDesignMode || !this.#profitOsAnalyticsEndpoint) return;

    document.addEventListener('visitorConsentCollected', this.#analyticsAllowed, { signal });
    let loading = false;
    let attempts = 0;
    const check = () => {
      if (signal.aborted) return;
      window.clearTimeout(this.#privacyTimer);
      this.#analyticsAllowed();
      if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') return;

      // Shopify and its feature loader can arrive after the popup component.
      this.#privacyTimer = window.setTimeout(check, RETRY_DELAY);
      if (loading || attempts >= 3 || typeof window.Shopify?.loadFeatures !== 'function') return;
      loading = true;
      attempts += 1;
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          loading = false;
          if (!error) check();
        });
      } catch (error) {
        loading = false;
      }
    };
    check();
  }

  #analyticsAllowed = () => {
    let allowed = false;
    const privacyReady = typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
    try {
      // Includes Shopify's regional settings. Missing/failed APIs fail closed.
      allowed = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (error) {
      // Optional analytics must never prevent a requested signup.
    }
    if (!allowed) {
      if (privacyReady) popupExperiment.revoke();
      if (this.dataset.experimentVariant === 'overlay' && this.dataset.experimentThemeRole === 'main') {
        this.dataset.experimentVariant = this.#mode === 'welcome' && this.dataset.experimentEnabled !== 'true'
          ? popupExperiment.presentation(this.dataset.pageType) : 'control';
      }
      this.#abortAnalyticsRequests();
      this.#analyticsSessionId = '';
      for (const key of [this.#analyticsSessionKey, this.#eligibleSessionKey]) {
        try {
          window.sessionStorage.removeItem(key);
        } catch (error) {
          // Storage may be disabled; consent enforcement does not depend on it.
        }
      }
    }
    if (allowed) this.#tryRemoteEnrollment();
    return allowed;
  };

  #abortAnalyticsRequests() {
    for (const controller of this.#analyticsRequests) controller.abort();
    this.#analyticsRequests.clear();
  }

  #track(eventName, properties = {}) {
    if (this.#isDesignMode || popupExperiment.isPreview()) return;

    const endpoint = this.#profitOsAnalyticsEndpoint;
    if (!endpoint || this.#abortController.signal.aborted || !this.#analyticsAllowed()) return;

    let controller;
    try {
      const countryCode =
        this.#detectedCountryCode || String(window.Shopify?.country || '').toUpperCase();
      const popupMode = properties.popup_mode || this.#mode;
      const experiment = popupMode === 'welcome' ? popupExperiment.context() : null;
      const basePayload = {
        eventId: this.#createAnalyticsId('event'),
        eventName,
        occurredAt: new Date().toISOString(),
        sessionId: this.#getAnalyticsSessionId(),
        popupMode,
        campaign:
          popupMode === 'international'
            ? this.dataset.internationalCampaign || 'international-waitlist-v1'
            : this.dataset.welcomeCampaign || WELCOME_CAMPAIGN,
        storefrontLocale: this.#storefrontLocale,
        pageType: this.dataset.pageType?.trim() || undefined,
        pathname: window.location.pathname,
        viewportCategory: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
        ...(experiment ? { experiment } : {}),
      };
      const funnelEvent = [
        'newsletter_popup_view',
        'newsletter_popup_dismiss',
        'newsletter_popup_submit_attempt',
        'newsletter_popup_submit_success',
        'newsletter_popup_submit_error',
      ].includes(eventName);
      const payload = addAnalyticsTrigger(
        basePayload,
        properties.trigger || (funnelEvent ? this.#openTrigger : '')
      );

      if (/^[A-Z]{2}$/.test(countryCode)) {
        payload.detectedCountryCode = countryCode;
      }

      const body = JSON.stringify(payload);

      // Unlike sendBeacon, fetch can be aborted when permission is revoked.
      controller = new AbortController();
      this.#analyticsRequests.add(controller);
      void fetch(endpoint, {
        method: 'POST',
        body,
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        keepalive: true,
        mode: 'cors',
        signal: controller.signal,
      }).catch(() => {}).finally(() => this.#analyticsRequests.delete(controller));
    } catch (error) {
      if (controller) this.#analyticsRequests.delete(controller);
      // Analytics is best-effort and must never interrupt the popup flow.
    }
  }

  #getAnalyticsSessionId() {
    const sharedSessionId = window.kokorunoAnalytics?.sessionId?.();
    if (sharedSessionId) return sharedSessionId;
    if (this.#analyticsSessionId) return this.#analyticsSessionId;
    const storedSessionId = this.#readSessionValue(this.#analyticsSessionKey);
    if (/^[A-Za-z0-9_-]{8,100}$/.test(storedSessionId)) {
      this.#analyticsSessionId = storedSessionId;
      return storedSessionId;
    }

    const sessionId = this.#createAnalyticsId('session');
    this.#analyticsSessionId = sessionId;
    this.#writeSessionValue(this.#analyticsSessionKey, sessionId);
    return sessionId;
  }

  #createAnalyticsId(prefix) {
    if (typeof crypto.randomUUID === 'function') {
      return `${prefix}_${crypto.randomUUID().replaceAll('-', '')}`;
    }

    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  }

  #readState(key) {
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  }

  #writeWelcomeState(update) {
    this.#writeState(this.#storageKey, update, this.dataset.welcomeCampaign || WELCOME_CAMPAIGN);
  }

  #writeInternationalState(update) {
    this.#writeState(this.#internationalStorageKey, update, 'international-waitlist-v1');
  }

  #writeState(key, update, campaign) {
    if (this.#isDesignMode) return;

    try {
      const state = { ...this.#readState(key), ...update, campaign };
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; the popup still works.
    }
  }

  #readSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  #writeSessionValue(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Session storage is an enhancement, not a requirement.
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
